import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconLink,
  IconListDetails,
  IconSearch,
  IconSettings,
  IconShield,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";
import { useOrganizationLimits } from "../../../../business/limits.hook";
import { LimitUsageBar } from "../../components/LimitIndicator";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const PlayerDocumentation = () => {
  const { classes } = useStyles();
  const { getLimit, loading: limitsLoading } = useOrganizationLimits([
    "REGISTERED_USERS",
    "ANONYMOUS_PLAYER_PROFILES",
    "SKILL_RATINGS",
  ]);
  const usersLimit = getLimit("REGISTERED_USERS");
  const anonymousLimit = getLimit("ANONYMOUS_PLAYER_PROFILES");
  const skillRatingsLimit = getLimit("SKILL_RATINGS");

  const activeLimits = [usersLimit, anonymousLimit, skillRatingsLimit].filter(
    Boolean,
  );

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Players Documentation"
        subtitle="Complete reference for the player profile management system"
        showBreadcrumbs={false}
      />

      {!limitsLoading && activeLimits.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
          {usersLimit && (
            <LimitUsageBar limit={usersLimit} label="Registered Users" />
          )}
          {anonymousLimit && (
            <LimitUsageBar limit={anonymousLimit} label="Anonymous Profiles" />
          )}
          {skillRatingsLimit && (
            <LimitUsageBar limit={skillRatingsLimit} label="Skill Ratings" />
          )}
        </SimpleGrid>
      )}

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#profile-management">Profile Management</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#custom-fields">Custom Fields</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#search">Search & Retrieval</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#skill-rating">Skill Rating</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#permissions">Permissions</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The Player system manages user profiles within an organization. Each
            player profile represents a participant who can join tournaments,
            appear on leaderboards, hold items and currencies, and interact with
            other platform features.
          </Text>
          <Text mb="md">
            Player profiles are linked to the organization and can optionally be
            linked to an authenticated account via the <Code>ownerId</Code>{" "}
            field. Unlinked profiles are considered anonymous.
          </Text>

          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Player Profile
  +-- username (searchable, display name)
  +-- description (profile bio)
  +-- customFields[] (key-value properties attached to the profile)
  +-- identities[] (linked identity provider accounts, e.g. Riot, Twitch)
  +-- owner (linked Account, optional)
  +-- organizationCustomId (external system ID, optional)
  +-- Skill Ratings[] (computed competitive ratings)
  +-- Match History[] (past tournament matches)
  +-- Currencies[] (gamification balances)
  +-- Items[] (gamification inventory)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            A player profile is scoped to an organization. The same account can
            have different player profiles in different organizations. Custom
            fields are defined at the organization level and can be used in
            tournament registration rules, leaderboard filtering, and other
            features.
          </Alert>
        </div>

        {/* ===================== PROFILE MANAGEMENT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUserPlus size={18} />}
            title="Profile Management"
            id="profile-management"
          />
          <Text mb="md">
            Player profiles can be created and managed through two approaches:
            admin-managed (via the console or admin API) and self-service (by
            authenticated users managing their own profile).
          </Text>

          <Title order={4} mb="xs">
            Admin: Create a Player Profile
          </Title>
          <Text mb="sm">
            Admins can create player profiles with full control over all fields
            including owner assignment and external IDs.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:create</Code>
          </Text>

          <GqlBlock>
            {`mutation ($input: CreatePlayerInput!) {
  createPlayer(input: $input) {
    id
    username
    description
    ownerId
    organizationCustomId
  }
}

# CreatePlayerInput:
# {
#   username: String!        - Display name (searchable)
#   description: String!     - Profile bio
#   ownerId: ID              - Link to an Account (omit for anonymous)
#   organizationCustomId: String  - External system ID
#   customFields: [PlayerCustomFieldInput!]!  - Key-value properties
# }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Admin: Update a Player Profile
          </Title>
          <Text mb="sm">
            Update any player profile by ID or by{" "}
            <Code>organizationCustomId</Code>. All fields are optional in the
            update input.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:update</Code>
          </Text>

          <GqlBlock>
            {`mutation ($id: ID, $customId: String, $input: UpdatePlayerInput!) {
  updatePlayer(id: $id, customId: $customId, input: $input) {
    id
    username
    description
    ownerId
    organizationCustomId
  }
}

# UpdatePlayerInput:
# {
#   username: String         - New display name
#   description: String      - New bio
#   ownerId: ID              - Change linked account
#   organizationCustomId: String  - Change external ID
#   customFields: [PlayerCustomFieldInput!]  - Update properties
# }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Admin: Delete a Player Profile
          </Title>
          <Text mb="sm">
            Delete a player profile by ID or <Code>organizationCustomId</Code>.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:delete</Code>
          </Text>

          <GqlBlock>
            {`mutation ($id: ID, $customId: ID) {
  deletePlayer(id: $id, customId: $customId)
  # Returns: Boolean!
}`}
          </GqlBlock>

          <Divider my="lg" />

          <Title order={4} mb="xs">
            Self-Service: Create or Update My Profile
          </Title>
          <Text mb="sm">
            Authenticated users can create or update their own player profile.
            If a profile already exists for the current user, it will be
            updated. Otherwise, a new profile is created.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:update:me</Code>
          </Text>

          <GqlBlock>
            {`mutation ($input: UpdateMePlayerInput!) {
  createOrUpdateMyPlayerProfile(input: $input) {
    id
    username
    description
  }
}

# UpdateMePlayerInput:
# {
#   username: String!        - Display name
#   description: String!     - Profile bio
#   customFields: [PlayerCustomFieldInput!]!  - Key-value properties
# }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            The self-service mutation automatically links the profile to the
            authenticated user's account. Unlike the admin mutation, it does not
            allow setting <Code>ownerId</Code> or{" "}
            <Code>organizationCustomId</Code>.
          </Alert>
        </div>

        {/* ===================== CUSTOM FIELDS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Custom Fields"
            id="custom-fields"
          />
          <Text mb="md">
            Custom fields are key-value pairs attached to a player profile. They
            provide a flexible way to store additional data like email, phone
            number, game-specific IDs, or any other property.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Use Cases
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Store game-specific player data (rank, level, region)
                </List.Item>
                <List.Item>Tournament registration rule conditions</List.Item>
                <List.Item>
                  Filtering and sorting players by properties
                </List.Item>
                <List.Item>
                  Integration with external systems via custom IDs
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Custom Field Structure
              </Text>
              <GqlBlock>
                {`input PlayerCustomFieldInput {
  property: String!  # Key identifier
  value: String!     # Value (always string)
}

# When querying, custom fields return:
type PropertyConfigWithValue {
  property: String!
  value: String!
  # ... additional config metadata
}`}
              </GqlBlock>
            </Card>
          </SimpleGrid>

          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mt="md"
          >
            Custom fields on player profiles use a simple key-value model (
            <Code>PlayerCustomFieldInput</Code>). This is different from
            tournament custom fields which use a richer schema with types,
            visibility, and editability settings.
          </Alert>
        </div>

        {/* ===================== SEARCH & RETRIEVAL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSearch size={18} />}
            title="Search & Retrieval"
            id="search"
          />
          <Text mb="md">
            The API provides multiple ways to find and retrieve player profiles
            depending on the use case.
          </Text>

          <Title order={4} mb="xs">
            Get a Single Player
          </Title>
          <Text mb="sm">
            Retrieve a player by any of three identifiers: internal ID, owner
            account ID, or organization custom ID.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:view</Code>
          </Text>

          <GqlBlock>
            {`query ($id: ID, $ownerId: ID, $customId: String) {
  player(id: $id, ownerId: $ownerId, customId: $customId) {
    id
    username
    description
    ownerId
    organizationCustomId
    customFields {
      property
      value
    }
    identities {
      provider
      properties { property, value }
    }
    createdAt
    updatedAt
  }
}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            List Players (Paginated)
          </Title>
          <Text mb="sm">
            Retrieve a paginated list of all players in the organization.
            Supports filtering by identity providers and their properties.
          </Text>

          <GqlBlock>
            {`query ($page: PageInfo!, $query: QueryPlayersInput,
       $ids: [ID!], $identityProviders: [ID!],
       $identityProviderProperties: [String!]) {
  players(
    page: $page
    query: $query
    ids: $ids
    identityProviders: $identityProviders
    identityProviderProperties: $identityProviderProperties
  ) {
    nodes {
      id
      username
      description
      ownerId
      organizationCustomId
      customFields { property, value }
      createdAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# QueryPlayersInput:
# { anonymous: Boolean }  - Filter anonymous (unlinked) profiles`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Search by Username
          </Title>
          <Text mb="sm">
            Search for players by username prefix. The search is
            case-insensitive and matches from the beginning of the username.
            Returns up to 20 results.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission: <Code>organization:players:view</Code>
          </Text>

          <GqlBlock>
            {`query ($username: String!) {
  searchPlayer(username: $username) {
    id
    username
    description
    ownerId
    organizationCustomId
  }
}

# Example: searching "alex" will match "AlexMog", "AlexLuke"
# but not "Mark" or "xAlex"`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            <Code>searchPlayer</Code> matches from the beginning of the username
            only (prefix search). For full-text search or filtering by other
            properties, use the <Code>players</Code> query with appropriate
            filters.
          </Alert>
        </div>

        {/* ===================== IDENTITY LINKING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLink size={18} />}
            title="Identity Linking & Management"
            id="identity-linking"
          />
          <Text mb="md">
            Identity linking allows you to connect external identity provider
            accounts (e.g. Riot, Twitch, Discord) to existing WellPlayed
            accounts. This flow uses the WellPlayed IDP service to securely
            handle the OAuth2 handshake with the external provider.
          </Text>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="md"
          >
            This flow is different from the direct API-based{" "}
            <Code>attachOrUpdateIdentityToAccount</Code> mutation. The IDP flow
            redirects the user through the external provider's OAuth2 consent
            screen, ensuring the identity is verified before being linked. This
            is the recommended approach for linking new identities.
          </Alert>

          <Title order={4} mb="xs">
            How It Works
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`1. Your app calls generateIdentityLinkUrl(identityProviderId)
   → Returns a URL pointing to the WellPlayed IDP service
   → A link session is stored in Redis (5 min TTL)

2. Redirect the user to the returned URL
   → IDP redirects the user to the external provider's OAuth2 consent screen

3. User authorizes on the external provider (e.g. Riot, Twitch)
   → External provider redirects back to the WellPlayed IDP callback

4. IDP exchanges the authorization code for an access token
   → Retrieves the user's identity (ID, properties) from the provider
   → Upserts the AccountIdentity record in the database

5. IDP redirects the user to the linkRedirectUrl configured on the identity provider
   → Appends ?success=true on success
   → Appends ?success=false&error=<error_code> on failure

Possible error codes:
  - identity_provider_error   → Failed to exchange token with the provider
  - identity_already_attached → This external identity is already linked to a different account`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Prerequisites
          </Title>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              An identity provider must be configured with a{" "}
              <Code>linkRedirectUrl</Code> in its OAuth2 configuration. This is
              the URL where users are sent after the linking process completes.
            </List.Item>
            <List.Item>
              The identity provider's OAuth2 provider must have the WellPlayed
              IDP link callback URL registered as an allowed redirect URI. This
              URL follows the pattern:{" "}
              <Code>{"https://<idp-host>/link/<identityProviderId>"}</Code>
            </List.Item>
            <List.Item>
              The user must be authenticated (have a valid account) before
              initiating the link.
            </List.Item>
            <List.Item>
              The authenticated account must belong to the same organization
              as the identity provider. Root accounts (which belong to no
              organization) and accounts from a different organization cannot
              link an identity and are rejected with{" "}
              <Code>ACCOUNT_NOT_IN_ORGANIZATION</Code>.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Step 1: Generate the Link URL
          </Title>
          <Text mb="sm">
            Call the <Code>generateIdentityLinkUrl</Code> mutation with the
            identity provider ID. The returned URL will redirect the user
            through the IDP linking flow.
          </Text>
          <GqlBlock>
            {`mutation ($identityProviderId: ID!) {
  generateIdentityLinkUrl(identityProviderId: $identityProviderId) {
    url
  }
}

# Returns: { url: "https://<idp-host>/link-redirect?linkToken=...&providerId=..." }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Step 2: Redirect the User
          </Title>
          <Text mb="sm">
            Redirect the user's browser to the returned <Code>url</Code>. The
            WellPlayed IDP will handle the OAuth2 flow with the external
            provider automatically.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// Example: redirect user in a web app
const { data } = await client.mutate({
  mutation: GENERATE_IDENTITY_LINK_URL,
  variables: { identityProviderId: "your-provider-id" },
});

window.location.href = data.generateIdentityLinkUrl.url;`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            Step 3: Handle the Redirect
          </Title>
          <Text mb="sm">
            After the flow completes, the user is redirected to the{" "}
            <Code>linkRedirectUrl</Code> configured on the identity provider.
            Parse the query parameters to determine the result:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// Success:
// https://your-app.com/link-callback?success=true

// Failure:
// https://your-app.com/link-callback?success=false&error=identity_already_attached
// https://your-app.com/link-callback?success=false&error=identity_provider_error`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mt="md"
          >
            The link session token expires after 5 minutes. If the user takes
            too long to complete the OAuth2 flow, they will need to start over
            by calling <Code>generateIdentityLinkUrl</Code> again.
          </Alert>

          <Divider my="lg" />

          <Title order={4} mb="xs">
            Redirect URL parameters
          </Title>
          <Text mb="sm">
            WellPlayed appends <Code>success</Code> (and <Code>error</Code>{" "}
            on failures) to the configured <Code>linkRedirectUrl</Code>:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`# Success
https://your-app.com/link-callback?success=true

# Failure cases
https://your-app.com/link-callback?success=false&error=identity_already_attached
https://your-app.com/link-callback?success=false&error=identity_provider_error`}
            </GqlBlock>
          </Card>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light" mb="md">
            If the configured <Code>linkRedirectUrl</Code> already contains a
            query string (e.g. <Code>?ref=1</Code>), params are appended
            with <Code>&amp;</Code> — no double-<Code>?</Code> is ever
            produced.
          </Alert>

          <Title order={4} mb="xs">
            Unlinking an identity{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text mb="sm">
            Use <Code>deleteAccountIdentity</Code> to remove a linked
            identity. Self-service (own account) requires no extra
            permissions — the default public grant covers it. Admin use
            requires <Code>organization:account:identity:delete</Code> on
            the target account.
          </Text>
          <GqlBlock>
            {`# Self-service: remove from the caller's own account
mutation ($identityProviderId: ID!) {
  deleteAccountIdentity(identityProviderId: $identityProviderId)
}

# Admin: remove from any account
mutation ($identityProviderId: ID!, $accountId: ID) {
  deleteAccountIdentity(
    identityProviderId: $identityProviderId
    accountId: $accountId
  )
}`}
          </GqlBlock>
          <Alert
            icon={<IconAlertCircle />}
            color="orange"
            variant="light"
            mt="md"
            mb="md"
          >
            Two identities can never be deleted regardless of permissions:
            the <strong>creation identity</strong> (oldest linked identity,
            error <Code>IDENTITY_CREATION_IDENTITY_NOT_DELETABLE</Code>) and
            the <strong>last remaining identity</strong> (error{" "}
            <Code>IDENTITY_LAST_IDENTITY_NOT_DELETABLE</Code>).
          </Alert>

          <Title order={4} mb="xs">
            isCreationIdentity field{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text mb="sm">
            The <Code>AccountIdentity</Code> type now exposes{" "}
            <Code>isCreationIdentity: Boolean</Code> (nullable). When{" "}
            <Code>true</Code>, this is the oldest linked identity of the
            account and cannot be deleted. The field is populated on the{" "}
            <Code>identities</Code> resolve field (accessible via{" "}
            <Code>player {"{ identities }"}</Code>); it is <Code>null</Code> on
            responses from <Code>accountIdentityRefresh</Code> and{" "}
            <Code>attachOrUpdateIdentityToAccount</Code>.
          </Text>
          <GqlBlock>
            {`query ($id: ID) {
  player(id: $id) {
    id
    username
    identities {
      providerId
      organizationIdentityProviderId
      isCreationIdentity
      createdAt
      properties { property, value }
    }
  }
}`}
          </GqlBlock>
        </div>

        {/* ===================== SKILL RATING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Skill Rating"
            id="skill-rating"
          />
          <Text mb="md">
            The Skill Rating system computes competitive ratings for players
            based on match results. It supports multiple rating algorithms and
            can optionally update a linked leaderboard.
          </Text>

          <Title order={4} mb="xs">
            Supported Algorithms
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
            <Card className={classes.card} p="md" radius="md">
              <Badge color="blue" mb="xs">
                BRADLEY_TERRY_PART
              </Badge>
              <Text size="sm">
                Partial Bradley-Terry model. Pairwise comparison model for
                ranking.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Badge color="blue" mb="xs">
                BREDLEY_TERRY_FULL
              </Badge>
              <Text size="sm">
                Full Bradley-Terry model with complete pairwise comparisons.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Badge color="blue" mb="xs">
                PLACKETT_LUCE
              </Badge>
              <Text size="sm">
                Plackett-Luce model for multi-team ranking scenarios.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Badge color="cyan" mb="xs">
                THURSTONE_MOSTELLER_FULL
              </Badge>
              <Text size="sm">
                Full Thurstone-Mosteller model based on normal distribution.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Badge color="cyan" mb="xs">
                THURSTONE_MOSTELLER_PART
              </Badge>
              <Text size="sm">
                Partial Thurstone-Mosteller model for ranking computation.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Create a Skill Rating
          </Title>
          <Text mb="sm">
            Create a new skill rating configuration for your organization.
            Optionally link it to a leaderboard to automatically update scores.
          </Text>

          <GqlBlock>
            {`mutation ($input: OrganizationSkillRatingCreateInput!) {
  organizationSkillRatingCreate(input: $input) {
    id
    name
    description
    type
    configuration { updateLeaderboardId }
  }
}

# OrganizationSkillRatingCreateInput:
# {
#   name: String!
#   description: String!
#   type: SkillRatingType!          - Algorithm to use
#   configuration: {
#     updateLeaderboardId: ID       - Optional linked leaderboard
#   }
# }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Register a Match
          </Title>
          <Text mb="sm">
            Register a match result to recompute skill ratings for all
            participating players. Team order matters: the first team is
            considered the winner, second team is second place, and so on.
          </Text>
          <Text mb="sm" size="sm">
            Requires permission:{" "}
            <Code>organization:skill-rating:register-match</Code>
          </Text>

          <GqlBlock>
            {`mutation ($organizationSkillRatingId: ID!,
         $input: OrganizationSkillRatingsRegisterMatchInput!) {
  organizationSkillRatingsRegisterMatch(
    organizationSkillRatingId: $organizationSkillRatingId
    input: $input
  ) {
    id
    oldValue
    newValue
    leaderboardPointsUpdate
  }
}

# OrganizationSkillRatingsRegisterMatchInput:
# {
#   teams: [{ players: [ID!]! }]!  - Ordered by placement (1st, 2nd, ...)
# }`}
          </GqlBlock>

          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mt="md"
          >
            Team placement order is critical. The first team in the array is
            treated as the winner. The response includes both the old and new
            rating values for each player, plus any leaderboard points that were
            updated.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Query Player Skill Ratings
          </Title>
          <Text mb="sm">
            Retrieve the current skill ratings for a specific player, optionally
            filtered to specific rating configurations.
          </Text>

          <GqlBlock>
            {`query ($playerId: ID!, $skillRatingIds: [ID!]) {
  playerSkillRatings(
    playerId: $playerId
    skillRatingIds: $skillRatingIds
  ) {
    ratingId
    playerProfileId
    value
    player { id, username }
    createdAt
    updatedAt
  }
}`}
          </GqlBlock>
        </div>

        {/* ===================== PERMISSIONS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconShield size={18} />}
            title="Permissions"
            id="permissions"
          />
          <Text mb="md">
            Player-related operations require specific organization permissions.
            These are configured through the IAM system.
          </Text>

          <Table striped highlightOnHover className={classes.statusTable}>
            <thead>
              <tr>
                <th>Permission</th>
                <th>Description</th>
                <th>Operations</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>organization:players:view</Code>
                </td>
                <td>View player profiles</td>
                <td>player, players, searchPlayer</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:players:create</Code>
                </td>
                <td>Create player profiles (admin)</td>
                <td>createPlayer</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:players:update</Code>
                </td>
                <td>Update any player profile</td>
                <td>updatePlayer</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:players:update:me</Code>
                </td>
                <td>Update own profile only</td>
                <td>createOrUpdateMyPlayerProfile</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:players:delete</Code>
                </td>
                <td>Delete player profiles</td>
                <td>deletePlayer</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:skill-rating:register-match</Code>
                </td>
                <td>Register skill rating matches</td>
                <td>organizationSkillRatingsRegisterMatch</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:account:identity:delete</Code>
                </td>
                <td>
                  Delete a linked identity from an account (included in the
                  default public grant for self-scoped resources)
                </td>
                <td>deleteAccountIdentity</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations related to the player
            system.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="player-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Player Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      player (single)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Retrieve a single player by ID, owner account ID, or
                      organization custom ID.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID, $ownerId: ID, $customId: String) {
  player(id: $id, ownerId: $ownerId, customId: $customId) {
    id, username, description, ownerId, organizationCustomId,
    customFields { property, value },
    identities { provider, properties { property, value } },
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      players (paginated list)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List all players with pagination. Supports filtering by
                      IDs, identity providers, and anonymous status.
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!, $query: QueryPlayersInput,
       $ids: [ID!], $identityProviders: [ID!],
       $identityProviderProperties: [String!]) {
  players(page: $page, query: $query, ids: $ids,
          identityProviders: $identityProviders,
          identityProviderProperties: $identityProviderProperties) {
    nodes { id, username, description, ownerId, organizationCustomId,
            customFields { property, value }, createdAt }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      searchPlayer (username search)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Prefix search on username. Case-insensitive, returns up to
                      20 results.
                    </Text>
                    <GqlBlock>
                      {`query ($username: String!) {
  searchPlayer(username: $username) {
    id, username, description, ownerId, organizationCustomId
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="player-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Player Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createPlayer
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Admin-created player profile with full field control.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreatePlayerInput!) {
  createPlayer(input: $input) {
    id, username, description, ownerId, organizationCustomId
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updatePlayer
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update a player by internal ID or organization custom ID.
                      All fields optional.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID, $customId: String, $input: UpdatePlayerInput!) {
  updatePlayer(id: $id, customId: $customId, input: $input) {
    id, username, description, ownerId, organizationCustomId
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deletePlayer
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Delete a player by internal ID or organization custom ID.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID, $customId: ID) {
  deletePlayer(id: $id, customId: $customId)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createOrUpdateMyPlayerProfile
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Self-service profile creation/update for the authenticated
                      user.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: UpdateMePlayerInput!) {
  createOrUpdateMyPlayerProfile(input: $input) {
    id, username, description
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Group gap="xs" mb="xs">
                      <Text fw={600}>deleteAccountIdentity</Text>
                      <Badge color="teal" variant="light" size="xs">
                        New
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed" mb="xs">
                      Remove a linked identity. Self-service (no{" "}
                      <Code>accountId</Code>) uses the default public grant.
                      Cannot delete the creation identity or the last
                      remaining identity.
                    </Text>
                    <GqlBlock>
                      {`mutation ($identityProviderId: ID!, $accountId: ID) {
  deleteAccountIdentity(
    identityProviderId: $identityProviderId
    accountId: $accountId   # omit for self-service
  )
  # Returns: Boolean!

  # Error codes:
  # IDENTITY_CREATION_IDENTITY_NOT_DELETABLE
  # IDENTITY_LAST_IDENTITY_NOT_DELETABLE
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="skill-rating-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    SKILL RATING
                  </Badge>
                  <Text>Skill Rating Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatings (list all)
                    </Text>
                    <GqlBlock>
                      {`query {
  organizationSkillRatings {
    id, name, description, type,
    configuration { updateLeaderboardId },
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRating (single)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  organizationSkillRating(id: $id) {
    id, name, description, type,
    configuration { updateLeaderboardId },
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerSkillRatings (player's ratings)
                    </Text>
                    <GqlBlock>
                      {`query ($playerId: ID!, $skillRatingIds: [ID!]) {
  playerSkillRatings(playerId: $playerId, skillRatingIds: $skillRatingIds) {
    ratingId, playerProfileId, value,
    player { id, username },
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingCreate
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: OrganizationSkillRatingCreateInput!) {
  organizationSkillRatingCreate(input: $input) {
    id, name, description, type,
    configuration { updateLeaderboardId }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: OrganizationSkillRatingUpdateInput!) {
  organizationSkillRatingUpdate(id: $id, input: $input) {
    id, name, description, type,
    configuration { updateLeaderboardId }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingDelete
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  organizationSkillRatingDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingsRegisterMatch
                    </Text>
                    <GqlBlock>
                      {`mutation ($organizationSkillRatingId: ID!,
         $input: OrganizationSkillRatingsRegisterMatchInput!) {
  organizationSkillRatingsRegisterMatch(
    organizationSkillRatingId: $organizationSkillRatingId, input: $input
  ) {
    id, oldValue, newValue, leaderboardPointsUpdate
  }
}

# Input: { teams: [{ players: [ID!]! }]! }
# Teams ordered by placement (1st team = winner)`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="player-facing-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="violet" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Player-Facing Queries</Text>
                  <Badge color="yellow" variant="light" size="xs">
                    Not used in console
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                >
                  These queries are available in the API but not used in the
                  admin console. They are intended for player-facing
                  applications and platform pages.
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      playerMatchesHistory
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Paginated match history for a specific player across all
                      tournaments. Useful for player profile pages.
                    </Text>
                    <GqlBlock>
                      {`query ($playerId: ID!, $page: PageInfo!) {
  playerMatchesHistory(playerId: $playerId, page: $page) {
    nodes {
      id, order, status,
      tournamentStepGroupRoundGameId,
      configuration { scoreFormula, variables { formulaName, displayName, defaultValue } },
      createdAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerCurrencies
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Retrieve a player's currency balances from the
                      gamification system.
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerCurrenciesFilterInput, $page: PageInfo) {
  playerCurrencies(filter: $filter, page: $page) {
    nodes { playerProfileId, currencyId, amount }
    pageInfo { hasNextPage, endCursor }
  }
}

# PlayerCurrenciesFilterInput:
# { currencyIds: [ID!], playerProfileIds: [ID!] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerItems
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Retrieve a player's item inventory from the gamification
                      system.
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerItemsFilterInput, $page: PageInfo) {
  playerItems(filter: $filter, page: $page) {
    nodes { playerProfileId, itemId, quantity }
    pageInfo { hasNextPage, endCursor }
  }
}

# PlayerItemsFilterInput:
# { itemIds: [ID!], playerProfileIds: [ID!] }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      playerShopProductPurshases
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Retrieve a player's shop purchase history.
                    </Text>
                    <GqlBlock>
                      {`query ($filter: PlayerShopProductPurshasesFilterInput, $page: PageInfo) {
  playerShopProductPurshases(filter: $filter, page: $page) {
    nodes { playerProfileId, createdAt }
    pageInfo { hasNextPage, endCursor }
  }
}

# PlayerShopProductPurshasesFilterInput:
# { playerProfileIds: [ID!] }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Player Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreatePlayerInput {
  username: String!
  description: String!
  ownerId: ID                          # Link to account (omit for anonymous)
  organizationCustomId: String         # External system ID
  customFields: [PlayerCustomFieldInput!]!
}

input UpdatePlayerInput {
  username: String
  description: String
  ownerId: ID
  organizationCustomId: String
  customFields: [PlayerCustomFieldInput!]
}

input UpdateMePlayerInput {
  username: String!
  description: String!
  customFields: [PlayerCustomFieldInput!]!
}

input PlayerCustomFieldInput {
  property: String!
  value: String!
}

input QueryPlayersInput {
  anonymous: Boolean     # Filter for anonymous (unlinked) profiles
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Skill Rating Inputs
                    </Text>
                    <GqlBlock>
                      {`input OrganizationSkillRatingCreateInput {
  name: String!
  description: String!
  type: SkillRatingType!
  configuration: SkillRatingConfigurationInput!
}

input OrganizationSkillRatingUpdateInput {
  name: String
  description: String
  type: SkillRatingType
  configuration: SkillRatingConfigurationInput
}

input SkillRatingConfigurationInput {
  updateLeaderboardId: ID    # Link to auto-update a leaderboard
}

input OrganizationSkillRatingsRegisterMatchInput {
  teams: [OrganizationSkillRatingsRegisterMatchTeamInput!]!
}

input OrganizationSkillRatingsRegisterMatchTeamInput {
  players: [ID!]!            # Player profile IDs in this team
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used in the player system. All types are
            generated from the GraphQL schema.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="entities">
              <Accordion.Control>Entity Structure</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Player
├── id: ID!
├── username: String!
├── description: String!
├── ownerId: ID                    # Linked account (null = anonymous)
├── organizationCustomId: ID       # External system identifier
├── customFields: [PropertyConfigWithValue!]!
│   ├── property: String!
│   └── value: String!
├── identities: [AccountIdentity!] # Identity provider links
│   ├── providerId: String!
│   ├── organizationIdentityProviderId: ID!
│   ├── isCreationIdentity: Boolean # true = oldest identity, cannot be deleted
│   ├── createdAt: DateTime!
│   ├── updatedAt: DateTime!
│   └── properties: [AccountIdentityProperty!]!
├── owner: Account                 # Resolved linked account
├── createdAt: DateTime!
└── updatedAt: DateTime!

OrganizationSkillRating
├── id: ID!
├── name: String!
├── description: String!
├── type: SkillRatingType!
├── organizationId: ID!
├── configuration: SkillRatingConfiguration
│   └── updateLeaderboardId: ID
├── createdAt: DateTime!
└── updatedAt: DateTime!

PlayerSkillRating
├── ratingId: ID!
├── playerProfileId: ID!
├── value: Float!
├── player: Player!
├── createdAt: DateTime!
└── updatedAt: DateTime!`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      SkillRatingType
                    </Text>
                    <GqlBlock>
                      {`BRADLEY_TERRY_PART
BREDLEY_TERRY_FULL
PLACKETT_LUCE
THURSTONE_MOSTELLER_FULL
THURSTONE_MOSTELLER_PART`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      PlayerConditionDataSource
                    </Text>
                    <GqlBlock>
                      {`IDENTITY_PROVIDER   # Data from linked IDP (Riot, Twitch, etc.)
PLAYER              # Data from player custom fields`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata (Data Service)"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            In addition to the player profile custom fields (simple key-value
            pairs via <Code>PlayerCustomFieldInput</Code>), player profiles also
            support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields (Data service)
              </Text>{" "}
              — Structured, typed fields with validation, visibility controls,
              and editability rules. Define schemas centrally in Data &gt;
              Custom Fields for the <Code>Player</Code> object type.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store with typed entries (String, Boolean,
              URL, Image URL) and visibility settings (Public or Private).
            </List.Item>
          </List>
          <GqlBlock>
            {`# Query custom field values for a player
query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}
# objectType: "Player"

# Query metadata for a player
query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
